import type Phaser from "phaser";
import type { ShellFrame } from "../ui/ShellFrame.js";
import type { BattlefieldRunner } from "./BattlefieldRunner.js";
import type { DungeonFlow } from "./DungeonFlow.js";
import type { SceneReadiness } from "./SceneReadiness.js";
import type { PerfTool } from "../diag/perfOverlay.js";
import { applyTargetFps, type AdaptiveFps } from "@yjsgames/perf";
import { FORCE_LOW_SPEC_RENDER_ENABLED } from "../env/appFlavor.js";

const LOW_SPEC_HUD_INTERVAL_MS = 250;
let lastLowSpecHudRefreshAt = Number.NEGATIVE_INFINITY;

export interface GameSceneFrameLoopDeps {
  readonly scene: Phaser.Scene;
  readonly readiness: SceneReadiness;
  readonly perf: PerfTool;
  readonly frame: ShellFrame;
  readonly runner: BattlefieldRunner;
  readonly dungeonFlow: DungeonFlow;
  readonly crazyGamesBannerGameplayPaused: boolean;
  readonly adaptiveFps: AdaptiveFps | undefined;
  readonly refreshHud: () => void;
  readonly onAutoFpsAdjusted?: (fps: number) => void;
}

export function updateGameSceneFrame(deps: GameSceneFrameLoopDeps): void {
  if (!deps.readiness.canRefresh) return;
  const p = deps.perf.probe;
  p.section("hud", () => {
    if (!FORCE_LOW_SPEC_RENDER_ENABLED) {
      deps.refreshHud();
      return;
    }
    const now = deps.scene.time.now;
    if (now < lastLowSpecHudRefreshAt || now - lastLowSpecHudRefreshAt >= LOW_SPEC_HUD_INTERVAL_MS) {
      lastLowSpecHudRefreshAt = now;
      deps.refreshHud();
    }
  });
  const aimLocatorOrigin = deps.runner.aimLocatorOrigin();
  const target = p.section("aim", () => deps.runner.aimLocator(aimLocatorOrigin.x, aimLocatorOrigin.y));
  const aimLineOrigin = target ? deps.runner.aimLineOrigin() : aimLocatorOrigin;
  p.section("aimLine", () => deps.frame.aimLine.update(deps.scene.time.now, aimLineOrigin, target));
  p.section("background", () => deps.frame.updateBackgroundScroll(deps.scene.game.loop.delta));
  if (!deps.crazyGamesBannerGameplayPaused) {
    p.section("collide", () => deps.runner.update(true));
  }
  if (deps.dungeonFlow.isActive()) {
    deps.frame.battlePressureBar.setVisible(false);
    deps.frame.battleDeadlineLine.setVisible(false);
    p.section("dungeon", () => deps.dungeonFlow.tick());
  } else if (FORCE_LOW_SPEC_RENDER_ENABLED) {
    deps.frame.battlePressureBar.setVisible(false);
    deps.frame.battleDeadlineLine.setVisible(false);
  } else {
    p.section("pressure", () => {
      const pressure = deps.runner.getDefeatPressureProgress();
      deps.frame.battlePressureBar.setVisible(pressure.visible);
      if (pressure.visible) deps.frame.battlePressureBar.update(pressure);
      deps.frame.battleDeadlineLine.update({
        visible: pressure.threat === "danger",
        y: pressure.deadlineY,
        threatBottomY: pressure.threatBottomY,
      });
    });
  }
  p.section("perfFlush", () => deps.perf.flush());
  const adaptiveFps = deps.adaptiveFps;
  if (adaptiveFps) {
    p.section("adaptiveFps", () => {
      const prev = adaptiveFps.current();
      const next = adaptiveFps.observe(deps.scene.game.loop.actualFps, deps.scene.game.loop.delta);
      if (next !== prev && applyTargetFps(deps.scene.game, next) && next < prev) {
        deps.onAutoFpsAdjusted?.(next);
      }
    });
  }
}
